import type {
  PaymentMonitoringExecutionStatus,
} from '@/services/admin/orderService'

export const MONITORING_OBSERVATION_INTERVAL_MS = 5_000
export const MONITORING_OBSERVATION_TIMEOUT_MS = 90_000

type MonitoringObservationTiming = {
  intervalMs?: number
  timeoutMs?: number
  now?: () => number
  sleep?: (milliseconds: number, signal: AbortSignal) => Promise<void>
}

type MonitoringObservationOptions = {
  signal: AbortSignal
  readStatus: (signal: AbortSignal) => Promise<PaymentMonitoringExecutionStatus>
  onStatus: (status: PaymentMonitoringExecutionStatus) => void
  timing?: MonitoringObservationTiming
}

export type MonitoringObservationResult = {
  status: PaymentMonitoringExecutionStatus | null
  timedOut: boolean
}

export const isOrderQueryForMode = (
  queryKey: readonly unknown[],
  mode: 'mainnet' | 'testnet'
): boolean => queryKey[0] === 'admin'
  && queryKey[1] === 'orders'
  && queryKey[queryKey.length - 1] === mode

export const hasCompletedMonitoringStateChange = (
  status: PaymentMonitoringExecutionStatus
): boolean => status.orders.some(order => order.complete
  && order.stateVersion !== null
  && order.stateVersion > order.baselineStateVersion)

const throwIfAborted = (signal: AbortSignal): void => {
  if (signal.aborted) {
    throw new DOMException('Monitoring observation aborted', 'AbortError')
  }
}

const abortableSleep = (milliseconds: number, signal: AbortSignal): Promise<void> =>
  new Promise((resolve, reject) => {
    const timeoutId = globalThis.setTimeout(() => {
      signal.removeEventListener('abort', handleAbort)
      resolve()
    }, milliseconds)
    const handleAbort = () => {
      globalThis.clearTimeout(timeoutId)
      reject(new DOMException('Monitoring observation aborted', 'AbortError'))
    }
    signal.addEventListener('abort', handleAbort, { once: true })
  })

export async function observeMonitoringExecution({
  signal,
  readStatus,
  onStatus,
  timing,
}: MonitoringObservationOptions): Promise<MonitoringObservationResult> {
  const intervalMs = timing?.intervalMs ?? MONITORING_OBSERVATION_INTERVAL_MS
  const timeoutMs = timing?.timeoutMs ?? MONITORING_OBSERVATION_TIMEOUT_MS
  const now = timing?.now ?? Date.now
  const sleep = timing?.sleep ?? abortableSleep
  const startedAt = now()
  let lastStatus: PaymentMonitoringExecutionStatus | null = null

  while (true) {
    throwIfAborted(signal)
    const remainingBeforeRead = timeoutMs - (now() - startedAt)
    if (remainingBeforeRead <= 0) {
      return { status: lastStatus, timedOut: true }
    }

    const readController = new AbortController()
    const abortRead = () => readController.abort(signal.reason)
    signal.addEventListener('abort', abortRead, { once: true })
    const readTimeoutId = globalThis.setTimeout(
      () => readController.abort(),
      remainingBeforeRead
    )
    let status: PaymentMonitoringExecutionStatus
    try {
      status = await readStatus(readController.signal)
    } catch (error) {
      if (signal.aborted) {
        throwIfAborted(signal)
      }
      if (readController.signal.aborted && now() - startedAt >= timeoutMs) {
        return { status: lastStatus, timedOut: true }
      }
      throw error
    } finally {
      globalThis.clearTimeout(readTimeoutId)
      signal.removeEventListener('abort', abortRead)
    }
    throwIfAborted(signal)
    lastStatus = status
    onStatus(status)

    if (status.complete) {
      return { status, timedOut: false }
    }

    const elapsedMs = now() - startedAt
    if (elapsedMs >= timeoutMs) {
      return { status, timedOut: true }
    }

    await sleep(Math.min(intervalMs, timeoutMs - elapsedMs), signal)
  }
}
